import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { FaDesktop, FaExpand, FaFont, FaMoon, FaPalette, FaSave, FaSquare, FaSun, FaUndo } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import './ThemeConfig.css';

// Default theme configuration
const DEFAULT_THEME = {
  light: {
    primary: '#1976D2',
    secondary: '#424242',
    accent: '#FF4081',
    background: '#FFFFFF',
    surface: '#FFFFFF',
    error: '#B00020',
    onPrimary: '#FFFFFF',
    onSecondary: '#FFFFFF',
    onBackground: '#000000',
    onSurface: '#000000',
    onError: '#FFFFFF'
  },
  dark: {
    primary: '#BB86FC',
    secondary: '#03DAC6',
    accent: '#FF4081',
    background: '#121212',
    surface: '#1E1E1E',
    error: '#CF6679',
    onPrimary: '#000000',
    onSecondary: '#000000',
    onBackground: '#FFFFFF',
    onSurface: '#FFFFFF',
    onError: '#000000'
  },
  typography: {
    fontFamily: 'Roboto',
    headingFontFamily: 'Roboto',
    fontSizeSmall: 12,
    fontSizeMedium: 14,
    fontSizeLarge: 18,
    fontSizeXLarge: 22,
    fontSizeHeading: 24,
    fontSizeDisplay: 32
  },
  button: {
    color: '#1976D2',
    textColor: '#FFFFFF',
    disabledColor: '#BDBDBD',
    disabledTextColor: '#757575',
    borderRadius: 8,
    elevation: 2
  },
  spacing: {
    xs: 4,
    small: 8,
    medium: 16,
    large: 24,
    xl: 32,
    xxl: 48
  },
  borderRadius: {
    small: 4,
    medium: 8,
    large: 12,
    xl: 16,
    round: 999
  },
  elevation: 2,
  isDarkModeEnabled: true,
  defaultThemeMode: 'system'
};

// Available font families
const FONT_FAMILIES = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Inter',
  'Nunito',
  'Raleway',
  'Source Sans Pro',
  'Ubuntu',
  'Playfair Display',
  'Merriweather',
  'PT Sans',
  'Oswald',
  'Quicksand'
];

const ThemeConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [themeConfig, setThemeConfig] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');
  const [previewMode, setPreviewMode] = useState('light');

  const fetchThemeData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch tenant info
      const tenantResponse = await axios.get(`${API_URL}/api/saas/tenants/${id}`);
      setTenant(tenantResponse.data.tenant);
      
      // Fetch theme config
      const themeResponse = await axios.get(`${API_URL}/api/theme/saas/tenants/${id}`);
      if (themeResponse.data.success && themeResponse.data.data.themeConfig) {
        setThemeConfig(themeResponse.data.data.themeConfig);
      }
    } catch (error) {
      console.error('Error fetching theme data:', error);
      toast.error('Error loading theme configuration');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch tenant and theme data
  useEffect(() => {
    fetchThemeData();
  }, [fetchThemeData]);

  // Handle color change
  const handleColorChange = (section, key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  // Handle typography change
  const handleTypographyChange = (key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      typography: {
        ...prev.typography,
        [key]: typeof prev.typography[key] === 'number' ? parseInt(value) || 0 : value
      }
    }));
  };

  // Handle button style change
  const handleButtonChange = (key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      button: {
        ...prev.button,
        [key]: typeof prev.button[key] === 'number' ? parseInt(value) || 0 : value
      }
    }));
  };

  // Handle spacing change
  const handleSpacingChange = (key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      spacing: {
        ...prev.spacing,
        [key]: parseInt(value) || 0
      }
    }));
  };

  // Handle border radius change
  const handleBorderRadiusChange = (key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      borderRadius: {
        ...prev.borderRadius,
        [key]: parseInt(value) || 0
      }
    }));
  };

  // Handle general setting change
  const handleGeneralChange = (key, value) => {
    setThemeConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Save theme
  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/theme/saas/tenants/${id}`, { themeConfig });
      toast.success('Theme configuration saved successfully!');
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error(error.response?.data?.message || 'Error saving theme configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset theme to defaults
  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the theme to defaults? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/theme/saas/tenants/${id}/reset`);
      setThemeConfig(DEFAULT_THEME);
      toast.success('Theme reset to defaults!');
    } catch (error) {
      console.error('Error resetting theme:', error);
      toast.error('Error resetting theme');
    } finally {
      setSaving(false);
    }
  };

  // Color input component
  const ColorInput = ({ label, value, onChange, description }) => (
    <div className="color-input-group">
      <label>{label}</label>
      <div className="color-input-wrapper">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="color-picker"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="color-text"
          placeholder="#000000"
        />
      </div>
      {description && <small className="color-description">{description}</small>}
    </div>
  );

  // Number input component
  const NumberInput = ({ label, value, onChange, min = 0, max = 100, unit = 'px' }) => (
    <div className="number-input-group">
      <label>{label}</label>
      <div className="number-input-wrapper">
        <input
          type="range"
          min={min}
          max={max}
          value={value || 0}
          onChange={(e) => onChange(e.target.value)}
          className="number-slider"
        />
        <div className="number-value">
          <input
            type="number"
            value={value || 0}
            onChange={(e) => onChange(e.target.value)}
            min={min}
            max={max}
            className="number-text"
          />
          <span className="number-unit">{unit}</span>
        </div>
      </div>
    </div>
  );

  // Get current preview colors
  const previewColors = previewMode === 'dark' ? themeConfig.dark : themeConfig.light;

  if (loading) {
    return <div className="loading">Loading theme configuration...</div>;
  }

  return (
    <div className="theme-config">
      <div className="page-header">
        <div className="header-info">
          <h1>Theme Configuration</h1>
          <p>Configure mobile app theme for <strong>{tenant?.name || 'Tenant'}</strong></p>
        </div>
        <div className="header-actions">
          <button onClick={handleReset} className="btn btn-secondary" disabled={saving}>
            <FaUndo /> Reset to Defaults
          </button>
          <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
            <FaSave /> {saving ? 'Saving...' : 'Save Theme'}
          </button>
        </div>
      </div>

      <div className="theme-editor">
        {/* Tabs */}
        <div className="editor-tabs">
          <button
            className={activeTab === 'colors' ? 'active' : ''}
            onClick={() => setActiveTab('colors')}
          >
            <FaPalette /> Colors
          </button>
          <button
            className={activeTab === 'typography' ? 'active' : ''}
            onClick={() => setActiveTab('typography')}
          >
            <FaFont /> Typography
          </button>
          <button
            className={activeTab === 'buttons' ? 'active' : ''}
            onClick={() => setActiveTab('buttons')}
          >
            <FaSquare /> Buttons
          </button>
          <button
            className={activeTab === 'spacing' ? 'active' : ''}
            onClick={() => setActiveTab('spacing')}
          >
            <FaExpand /> Spacing
          </button>
        </div>

        <div className="editor-content">
          {/* Left: Settings */}
          <div className="editor-settings">
            {/* Colors Tab */}
            {activeTab === 'colors' && (
              <div className="settings-section">
                {/* Theme Mode Toggle */}
                <div className="theme-mode-toggle">
                  <button
                    className={previewMode === 'light' ? 'active' : ''}
                    onClick={() => setPreviewMode('light')}
                  >
                    <FaSun /> Light Theme
                  </button>
                  <button
                    className={previewMode === 'dark' ? 'active' : ''}
                    onClick={() => setPreviewMode('dark')}
                  >
                    <FaMoon /> Dark Theme
                  </button>
                </div>

                <h3>{previewMode === 'light' ? 'Light' : 'Dark'} Theme Colors</h3>
                
                <div className="color-grid">
                  <ColorInput
                    label="Primary"
                    value={themeConfig[previewMode]?.primary}
                    onChange={(v) => handleColorChange(previewMode, 'primary', v)}
                    description="Main brand color"
                  />
                  <ColorInput
                    label="Secondary"
                    value={themeConfig[previewMode]?.secondary}
                    onChange={(v) => handleColorChange(previewMode, 'secondary', v)}
                    description="Secondary brand color"
                  />
                  <ColorInput
                    label="Accent"
                    value={themeConfig[previewMode]?.accent}
                    onChange={(v) => handleColorChange(previewMode, 'accent', v)}
                    description="Accent/highlight color"
                  />
                  <ColorInput
                    label="Background"
                    value={themeConfig[previewMode]?.background}
                    onChange={(v) => handleColorChange(previewMode, 'background', v)}
                    description="App background"
                  />
                  <ColorInput
                    label="Surface"
                    value={themeConfig[previewMode]?.surface}
                    onChange={(v) => handleColorChange(previewMode, 'surface', v)}
                    description="Card/surface background"
                  />
                  <ColorInput
                    label="Error"
                    value={themeConfig[previewMode]?.error}
                    onChange={(v) => handleColorChange(previewMode, 'error', v)}
                    description="Error states"
                  />
                </div>

                <h4>Text Colors (On Surface)</h4>
                <div className="color-grid">
                  <ColorInput
                    label="On Primary"
                    value={themeConfig[previewMode]?.onPrimary}
                    onChange={(v) => handleColorChange(previewMode, 'onPrimary', v)}
                    description="Text on primary"
                  />
                  <ColorInput
                    label="On Secondary"
                    value={themeConfig[previewMode]?.onSecondary}
                    onChange={(v) => handleColorChange(previewMode, 'onSecondary', v)}
                    description="Text on secondary"
                  />
                  <ColorInput
                    label="On Background"
                    value={themeConfig[previewMode]?.onBackground}
                    onChange={(v) => handleColorChange(previewMode, 'onBackground', v)}
                    description="Text on background"
                  />
                  <ColorInput
                    label="On Surface"
                    value={themeConfig[previewMode]?.onSurface}
                    onChange={(v) => handleColorChange(previewMode, 'onSurface', v)}
                    description="Text on surface"
                  />
                  <ColorInput
                    label="On Error"
                    value={themeConfig[previewMode]?.onError}
                    onChange={(v) => handleColorChange(previewMode, 'onError', v)}
                    description="Text on error"
                  />
                </div>

                {/* General Settings */}
                <h4>General Settings</h4>
                <div className="general-settings">
                  <div className="form-group">
                    <label>Dark Mode Enabled</label>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={themeConfig.isDarkModeEnabled}
                        onChange={(e) => handleGeneralChange('isDarkModeEnabled', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <small>Allow users to switch to dark mode</small>
                  </div>
                  
                  <div className="form-group">
                    <label>Default Theme Mode</label>
                    <div className="mode-selector">
                      <button
                        className={themeConfig.defaultThemeMode === 'light' ? 'active' : ''}
                        onClick={() => handleGeneralChange('defaultThemeMode', 'light')}
                      >
                        <FaSun /> Light
                      </button>
                      <button
                        className={themeConfig.defaultThemeMode === 'dark' ? 'active' : ''}
                        onClick={() => handleGeneralChange('defaultThemeMode', 'dark')}
                      >
                        <FaMoon /> Dark
                      </button>
                      <button
                        className={themeConfig.defaultThemeMode === 'system' ? 'active' : ''}
                        onClick={() => handleGeneralChange('defaultThemeMode', 'system')}
                      >
                        <FaDesktop /> System
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Typography Tab */}
            {activeTab === 'typography' && (
              <div className="settings-section">
                <h3>Typography Settings</h3>
                
                <div className="form-group">
                  <label>Primary Font Family</label>
                  <select
                    value={themeConfig.typography?.fontFamily || 'Roboto'}
                    onChange={(e) => handleTypographyChange('fontFamily', e.target.value)}
                  >
                    {FONT_FAMILIES.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                  <small>Used for body text</small>
                </div>

                <div className="form-group">
                  <label>Heading Font Family</label>
                  <select
                    value={themeConfig.typography?.headingFontFamily || 'Roboto'}
                    onChange={(e) => handleTypographyChange('headingFontFamily', e.target.value)}
                  >
                    {FONT_FAMILIES.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                  <small>Used for headings and titles</small>
                </div>

                <h4>Font Sizes</h4>
                <div className="spacing-grid">
                  <NumberInput
                    label="Small"
                    value={themeConfig.typography?.fontSizeSmall}
                    onChange={(v) => handleTypographyChange('fontSizeSmall', v)}
                    min={8}
                    max={20}
                    unit="sp"
                  />
                  <NumberInput
                    label="Medium"
                    value={themeConfig.typography?.fontSizeMedium}
                    onChange={(v) => handleTypographyChange('fontSizeMedium', v)}
                    min={10}
                    max={24}
                    unit="sp"
                  />
                  <NumberInput
                    label="Large"
                    value={themeConfig.typography?.fontSizeLarge}
                    onChange={(v) => handleTypographyChange('fontSizeLarge', v)}
                    min={14}
                    max={32}
                    unit="sp"
                  />
                  <NumberInput
                    label="X-Large"
                    value={themeConfig.typography?.fontSizeXLarge}
                    onChange={(v) => handleTypographyChange('fontSizeXLarge', v)}
                    min={16}
                    max={40}
                    unit="sp"
                  />
                  <NumberInput
                    label="Heading"
                    value={themeConfig.typography?.fontSizeHeading}
                    onChange={(v) => handleTypographyChange('fontSizeHeading', v)}
                    min={18}
                    max={48}
                    unit="sp"
                  />
                  <NumberInput
                    label="Display"
                    value={themeConfig.typography?.fontSizeDisplay}
                    onChange={(v) => handleTypographyChange('fontSizeDisplay', v)}
                    min={24}
                    max={64}
                    unit="sp"
                  />
                </div>
              </div>
            )}

            {/* Buttons Tab */}
            {activeTab === 'buttons' && (
              <div className="settings-section">
                <h3>Button Styles</h3>
                
                <div className="color-grid">
                  <ColorInput
                    label="Button Color"
                    value={themeConfig.button?.color}
                    onChange={(v) => handleButtonChange('color', v)}
                    description="Primary button background"
                  />
                  <ColorInput
                    label="Button Text Color"
                    value={themeConfig.button?.textColor}
                    onChange={(v) => handleButtonChange('textColor', v)}
                    description="Primary button text"
                  />
                  <ColorInput
                    label="Disabled Color"
                    value={themeConfig.button?.disabledColor}
                    onChange={(v) => handleButtonChange('disabledColor', v)}
                    description="Disabled button background"
                  />
                  <ColorInput
                    label="Disabled Text Color"
                    value={themeConfig.button?.disabledTextColor}
                    onChange={(v) => handleButtonChange('disabledTextColor', v)}
                    description="Disabled button text"
                  />
                </div>

                <div className="spacing-grid">
                  <NumberInput
                    label="Border Radius"
                    value={themeConfig.button?.borderRadius}
                    onChange={(v) => handleButtonChange('borderRadius', v)}
                    min={0}
                    max={32}
                    unit="dp"
                  />
                  <NumberInput
                    label="Elevation"
                    value={themeConfig.button?.elevation}
                    onChange={(v) => handleButtonChange('elevation', v)}
                    min={0}
                    max={24}
                    unit="dp"
                  />
                </div>
              </div>
            )}

            {/* Spacing Tab */}
            {activeTab === 'spacing' && (
              <div className="settings-section">
                <h3>Spacing</h3>
                <div className="spacing-grid">
                  <NumberInput
                    label="XS"
                    value={themeConfig.spacing?.xs}
                    onChange={(v) => handleSpacingChange('xs', v)}
                    min={0}
                    max={16}
                    unit="dp"
                  />
                  <NumberInput
                    label="Small"
                    value={themeConfig.spacing?.small}
                    onChange={(v) => handleSpacingChange('small', v)}
                    min={0}
                    max={24}
                    unit="dp"
                  />
                  <NumberInput
                    label="Medium"
                    value={themeConfig.spacing?.medium}
                    onChange={(v) => handleSpacingChange('medium', v)}
                    min={0}
                    max={32}
                    unit="dp"
                  />
                  <NumberInput
                    label="Large"
                    value={themeConfig.spacing?.large}
                    onChange={(v) => handleSpacingChange('large', v)}
                    min={0}
                    max={48}
                    unit="dp"
                  />
                  <NumberInput
                    label="XL"
                    value={themeConfig.spacing?.xl}
                    onChange={(v) => handleSpacingChange('xl', v)}
                    min={0}
                    max={64}
                    unit="dp"
                  />
                  <NumberInput
                    label="XXL"
                    value={themeConfig.spacing?.xxl}
                    onChange={(v) => handleSpacingChange('xxl', v)}
                    min={0}
                    max={96}
                    unit="dp"
                  />
                </div>

                <h3>Border Radius</h3>
                <div className="spacing-grid">
                  <NumberInput
                    label="Small"
                    value={themeConfig.borderRadius?.small}
                    onChange={(v) => handleBorderRadiusChange('small', v)}
                    min={0}
                    max={16}
                    unit="dp"
                  />
                  <NumberInput
                    label="Medium"
                    value={themeConfig.borderRadius?.medium}
                    onChange={(v) => handleBorderRadiusChange('medium', v)}
                    min={0}
                    max={24}
                    unit="dp"
                  />
                  <NumberInput
                    label="Large"
                    value={themeConfig.borderRadius?.large}
                    onChange={(v) => handleBorderRadiusChange('large', v)}
                    min={0}
                    max={32}
                    unit="dp"
                  />
                  <NumberInput
                    label="XL"
                    value={themeConfig.borderRadius?.xl}
                    onChange={(v) => handleBorderRadiusChange('xl', v)}
                    min={0}
                    max={48}
                    unit="dp"
                  />
                </div>

                <h3>General</h3>
                <NumberInput
                  label="Default Elevation"
                  value={themeConfig.elevation}
                  onChange={(v) => handleGeneralChange('elevation', parseInt(v) || 0)}
                  min={0}
                  max={24}
                  unit="dp"
                />
              </div>
            )}
          </div>

          {/* Right: Preview */}
          <div className="editor-preview">
            <h3>Live Preview</h3>
            <div 
              className="preview-container"
              style={{ 
                backgroundColor: previewColors?.background,
                fontFamily: themeConfig.typography?.fontFamily
              }}
            >
              {/* Mobile Frame */}
              <div className="mobile-frame">
                {/* Status Bar */}
                <div 
                  className="status-bar"
                  style={{ backgroundColor: previewColors?.primary }}
                >
                  <span style={{ color: previewColors?.onPrimary }}>9:41</span>
                </div>
                
                {/* App Bar */}
                <div 
                  className="app-bar"
                  style={{ 
                    backgroundColor: previewColors?.primary,
                    color: previewColors?.onPrimary,
                    fontFamily: themeConfig.typography?.headingFontFamily
                  }}
                >
                  <span>App Title</span>
                </div>

                {/* Content */}
                <div 
                  className="preview-content"
                  style={{ 
                    backgroundColor: previewColors?.background,
                    padding: `${themeConfig.spacing?.medium || 16}px`
                  }}
                >
                  {/* Card */}
                  <div 
                    className="preview-card"
                    style={{ 
                      backgroundColor: previewColors?.surface,
                      borderRadius: `${themeConfig.borderRadius?.large || 12}px`,
                      boxShadow: `0 ${themeConfig.elevation || 2}px ${(themeConfig.elevation || 2) * 2}px rgba(0,0,0,0.1)`,
                      marginBottom: `${themeConfig.spacing?.medium || 16}px`,
                      padding: `${themeConfig.spacing?.medium || 16}px`
                    }}
                  >
                    <h4 
                      style={{ 
                        color: previewColors?.onSurface,
                        fontSize: `${themeConfig.typography?.fontSizeHeading || 24}px`,
                        fontFamily: themeConfig.typography?.headingFontFamily,
                        marginBottom: `${themeConfig.spacing?.small || 8}px`
                      }}
                    >
                      Card Title
                    </h4>
                    <p 
                      style={{ 
                        color: previewColors?.onSurface,
                        fontSize: `${themeConfig.typography?.fontSizeMedium || 14}px`,
                        opacity: 0.7
                      }}
                    >
                      This is sample body text showing how content will appear in the mobile app.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="preview-buttons" style={{ marginBottom: `${themeConfig.spacing?.medium || 16}px` }}>
                    <button 
                      className="preview-button primary"
                      style={{ 
                        backgroundColor: themeConfig.button?.color,
                        color: themeConfig.button?.textColor,
                        borderRadius: `${themeConfig.button?.borderRadius || 8}px`,
                        boxShadow: `0 ${themeConfig.button?.elevation || 2}px ${(themeConfig.button?.elevation || 2) * 2}px rgba(0,0,0,0.2)`,
                        padding: `${themeConfig.spacing?.small || 8}px ${themeConfig.spacing?.medium || 16}px`,
                        fontSize: `${themeConfig.typography?.fontSizeMedium || 14}px`
                      }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="preview-button secondary"
                      style={{ 
                        backgroundColor: 'transparent',
                        color: previewColors?.primary,
                        border: `2px solid ${previewColors?.primary}`,
                        borderRadius: `${themeConfig.button?.borderRadius || 8}px`,
                        padding: `${themeConfig.spacing?.small || 8}px ${themeConfig.spacing?.medium || 16}px`,
                        fontSize: `${themeConfig.typography?.fontSizeMedium || 14}px`
                      }}
                    >
                      Secondary
                    </button>
                    <button 
                      className="preview-button disabled"
                      style={{ 
                        backgroundColor: themeConfig.button?.disabledColor,
                        color: themeConfig.button?.disabledTextColor,
                        borderRadius: `${themeConfig.button?.borderRadius || 8}px`,
                        padding: `${themeConfig.spacing?.small || 8}px ${themeConfig.spacing?.medium || 16}px`,
                        fontSize: `${themeConfig.typography?.fontSizeMedium || 14}px`
                      }}
                      disabled
                    >
                      Disabled
                    </button>
                  </div>

                  {/* Accent Element */}
                  <div 
                    className="preview-accent"
                    style={{ 
                      backgroundColor: previewColors?.accent,
                      color: '#FFFFFF',
                      borderRadius: `${themeConfig.borderRadius?.round || 999}px`,
                      padding: `${themeConfig.spacing?.small || 8}px ${themeConfig.spacing?.medium || 16}px`,
                      display: 'inline-block',
                      fontSize: `${themeConfig.typography?.fontSizeSmall || 12}px`
                    }}
                  >
                    Accent Badge
                  </div>

                  {/* Error State */}
                  <div 
                    className="preview-error"
                    style={{ 
                      backgroundColor: previewColors?.error,
                      color: previewColors?.onError,
                      borderRadius: `${themeConfig.borderRadius?.medium || 8}px`,
                      padding: `${themeConfig.spacing?.small || 8}px`,
                      marginTop: `${themeConfig.spacing?.medium || 16}px`,
                      fontSize: `${themeConfig.typography?.fontSizeSmall || 12}px`
                    }}
                  >
                    Error message example
                  </div>
                </div>

                {/* Bottom Navigation */}
                <div 
                  className="bottom-nav"
                  style={{ 
                    backgroundColor: previewColors?.surface,
                    borderTop: `1px solid ${previewColors?.onSurface}20`
                  }}
                >
                  <div className="nav-item active" style={{ color: previewColors?.primary }}>
                    <span>Home</span>
                  </div>
                  <div className="nav-item" style={{ color: previewColors?.onSurface, opacity: 0.6 }}>
                    <span>Search</span>
                  </div>
                  <div className="nav-item" style={{ color: previewColors?.onSurface, opacity: 0.6 }}>
                    <span>Profile</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="form-actions">
        <button 
          onClick={() => navigate(`/tenants/${id}`)} 
          className="btn btn-secondary"
        >
          Back to Tenant
        </button>
      </div>
    </div>
  );
};

export default ThemeConfig;

